'use client'

import type { EnvVarGetter } from './env'

import {
  parse as defaultParse,
  stringify as defaultStringify,
  type Parser,
  type Stringifier
} from './json'
import { cookies, decodeLocalStorage, encodeLocalStorage } from './storage'

/*//////////////////////////////////////////////////////////////
                            COOKIES
//////////////////////////////////////////////////////////////*/

export function setCookie<T>(name: string, value: T, stringify: Stringifier<T> = defaultStringify) {
  if (typeof document === 'undefined') {
    console.debug(`Tried to setCookie(${name}, ...) on undefined document`)
    return
  }

  cookies.set(name, stringify(value))
}

export function getCookie<T = unknown>(name: string, parse: Parser<T> = defaultParse) {
  if (typeof document === 'undefined') {
    console.debug(`Tried to getCookie(${name}) from undefined document`)
    return undefined
  }

  const value = cookies.get(name)
  return value === undefined ? undefined : parse(value)
}

// oxlint-disable-next-line typescript/no-unused-vars
const _getCookieTypecheck: EnvVarGetter<unknown> = getCookie

/*//////////////////////////////////////////////////////////////
                          LOCALSTORAGE
//////////////////////////////////////////////////////////////*/

function isLocalStorageAvailable(): boolean {
  return typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
}

export function setLocalStorage<T>(
  name: string,
  value: T,
  stringify: Stringifier<T> = defaultStringify
) {
  if (!isLocalStorageAvailable()) {
    console.debug(`Tried to setLocalStorage(${name}, ...) on undefined localStorage`)
    return
  }

  try {
    localStorage.setItem(name, encodeLocalStorage(name, value, stringify))
  } catch {
    console.debug(`Tried to setLocalStorage(${name}, ...) on unavailable localStorage`)
  }
}

export function getLocalStorage<T = unknown>(name: string, parse: Parser<T> = defaultParse) {
  if (!isLocalStorageAvailable()) {
    console.debug(`Tried to getLocalStorage(${name}) from undefined localStorage`)
    return undefined
  }

  try {
    const value = localStorage.getItem(name)
    return value === null ? undefined : decodeLocalStorage(name, value, parse)
  } catch {
    console.debug(`Tried to getLocalStorage(${name}) from unavailable localStorage`)
    return undefined
  }
}

// oxlint-disable-next-line typescript/no-unused-vars
const _getLocalStorageTypecheck: EnvVarGetter<unknown> = getLocalStorage
